import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import type { ProblemAddressResponse } from '@/core/generated/api';
import { problemAddressService } from '@/entities/ProblemAddress/api/problemAddressService';
import { AddressPointEditor } from '@/features/verifyAddressPoint/ui/AddressPointEditor';
import { confirmUnsavedChanges } from '@/shared/lib/confirmUnsavedChanges';
import { Modal } from '@/shared/ui/Modal/Modal';

interface AddressRecordActionDialogProps {
    record: ProblemAddressResponse;
    onClose: () => void;
    onChanged: () => Promise<void>;
}

/** Проверяет координаты в списке с защитой изменений и переходом к следующему дому */
export const AddressRecordActionDialog = (
    { record, onClose, onChanged }: AddressRecordActionDialogProps
) => {
    const [point, setPoint] = useState(record);
    const [dirty, setDirty] = useState(false);
    const [busy, setBusy] = useState(false);

    /** Учитывает черновик и текущую отправку формы */
    const updateState = useCallback((isDirty: boolean, isBusy: boolean) => {
        setDirty(isDirty);
        setBusy(isBusy);
    }, []);

    /** Обновляет список и продолжает проверку следующего дома по запросу оператора */
    const saved = async (updated: ProblemAddressResponse, openNext: boolean): Promise<void> => {
        setPoint(updated);
        await onChanged();

        if (!openNext) return;
        try {
            const next = await problemAddressService.getList({
                isVerified: false,
                takeCount: 1,
                ascending: true
            });

            const id = next.addresses?.[0]?.id;

            if (!id) {
                onClose();

                return;
            }
            setPoint(await problemAddressService.getDetails(id));
        } catch {
            toast.info('Координаты сохранены. Не удалось открыть следующий адрес');
        }
    };

    return <Modal
        visible
        title="Проверка координат"
        size="xl"
        closable={!busy}
        onHide={() => { if (!busy) confirmUnsavedChanges(dirty, onClose); }}
    >
        <AddressPointEditor
            key={`${point.id}:${point.version}`}
            point={point}
            onSaved={saved}
            onStateChange={updateState}
        />
    </Modal>;
};
